import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fifoService } from '../../services/fifoService';
import { toast } from 'react-toastify';
import PageLoader from '../../components/PageLoader';
import { API_URL } from '../../config/api';

// Checkbox Options
const JOB_OPTIONS = [
    "ทดสอบเครื่องล้างภาชนะ (ใหม่)",
    "ทดสอบเครื่องล้างภาชนะ (เก่า)",
    "ทดสอบเครื่องล้างภาชนะ (เก่า) Repair and Modify",
    "ตรวจสอบอุปกรณ์แถมภายในเครื่อง",
    "ทดสอบเครื่องทำความสะอาดด้านพื้น",
    "ทดสอบใช้งานโมบาย",
    "ทดสอบเครื่องจักรอุปกรณ์",
    "อื่นๆ"
];

const RequestMachine = () => {
    const navigate = useNavigate();

    // Search params
    const [searchQuery, setSearchQuery] = useState('');

    // Data State
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cart, setCart] = useState([]);

    // Request Form
    const [requestData, setRequestData] = useState({
        customerName: '',
        customerAddress: '',
        wantDate: '',
        qcmDate: '', // "ต้องการรับเครื่องจากหน่วยงาน QCM วันที่" — distinct from wantDate
        remark: '',
        departmentExpense: '', // "เก็บค่าใช้จ่ายที่" — printed on the job notification PDF
        jobDetails: [], // Array of strings
        otherJobDetail: ''
    });

    // Printed Request Data (for the modal/print view)
    const [submittedRequest, setSubmittedRequest] = useState(null);
    const printRef = useRef();

    // Manual stock-data refresh (pulls ERP_QTY_Serial fresh from NetSuite).
    // retryAfterMs counts down locally between status polls so the button
    // re-enables itself without needing a page reload.
    const [refreshStatus, setRefreshStatus] = useState({ lastSyncedAt: null, onCooldown: false, retryAfterMs: 0 });
    const [refreshing, setRefreshing] = useState(false);

    // fetch all on mount
    useEffect(() => {
        fetchData();
        fetchRefreshStatus();
    }, []);

    // Tick the cooldown down locally once a second instead of re-polling the
    // server, so the button flips back to enabled right on time.
    useEffect(() => {
        if (!refreshStatus.onCooldown) return undefined;
        const interval = setInterval(() => {
            setRefreshStatus(prev => {
                const next = Math.max(0, prev.retryAfterMs - 1000);
                return { ...prev, retryAfterMs: next, onCooldown: next > 0 };
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [refreshStatus.onCooldown]);

    const fetchRefreshStatus = async () => {
        try {
            const status = await fifoService.getInventoryRefreshStatus();
            setRefreshStatus(status);
        } catch (error) {
            console.error('Failed to fetch inventory refresh status', error);
        }
    };

    const handleRefreshInventory = async () => {
        setRefreshing(true);
        try {
            const result = await fifoService.refreshInventoryData();
            toast.success(`อัปเดตข้อมูลสต็อกสำเร็จ (${result.rowsInserted} รายการ)`);
            setRefreshStatus({ lastSyncedAt: result.lastSyncedAt, onCooldown: true, retryAfterMs: 10 * 60 * 1000 });
            fetchData(searchQuery);
        } catch (error) {
            if (error.response?.status === 429) {
                toast.warning(error.response.data.error);
                setRefreshStatus(error.response.data);
            } else {
                toast.error('ไม่สามารถรีเฟรชข้อมูลได้ กรุณาลองใหม่อีกครั้ง');
            }
        } finally {
            setRefreshing(false);
        }
    };

    const formatTimeAgo = (dateStr) => {
        if (!dateStr) return 'ยังไม่เคยอัปเดต';
        const diffMs = Date.now() - new Date(dateStr).getTime();
        const mins = Math.floor(diffMs / 60000);
        if (mins < 1) return 'เมื่อสักครู่';
        if (mins < 60) return `${mins} นาทีที่แล้ว`;
        const hours = Math.floor(mins / 60);
        if (hours < 24) return `${hours} ชั่วโมงที่แล้ว`;
        return new Date(dateStr).toLocaleDateString('th-TH');
    };

    const formatCountdown = (ms) => {
        const totalSeconds = Math.ceil(ms / 1000);
        const m = Math.floor(totalSeconds / 60);
        const s = totalSeconds % 60;
        return `${m}:${String(s).padStart(2, '0')}`;
    };

    const fetchData = async (query = '') => {
        setLoading(true);
        try {
            const data = await fifoService.searchProducts(query ? { search: query } : {});
            setResults(data);
        } catch (error) {
            toast.error('ไม่สามารถโหลดข้อมูลสินค้าได้');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchData(searchQuery);
    };

    const handleReset = () => {
        setSearchQuery('');
        fetchData('');
    };

    const addToCart = (serialItem) => {
        if (cart.find(item => item.serial === serialItem.serial)) {
            toast.warning('สินค้านี้ถูกเลือกแล้ว');
            return;
        }
        setCart([...cart, serialItem]);
        toast.success(`เลือก ${serialItem.serial} แล้ว`);
    };

    const removeFromCart = (serial) => {
        setCart(cart.filter(item => item.serial !== serial));
    };

    const toggleJobDetail = (option) => {
        const current = requestData.jobDetails;
        if (current.includes(option)) {
            setRequestData({ ...requestData, jobDetails: current.filter(o => o !== option) });
        } else {
            setRequestData({ ...requestData, jobDetails: [...current, option] });
        }
    };

    const handleSubmitRequest = async () => {
        if (cart.length === 0) {
            toast.error('กรุณาเลือกเครื่องอย่างน้อย 1 รายการ');
            return;
        }
        if (!requestData.customerName || !requestData.wantDate) {
            toast.error('กรุณาระบุชื่อลูกค้าและวันที่ต้องการใช้ของ');
            return;
        }

        const items = cart.map(item => ({
            productId: item.itemCode,
            quantity: 1,
            description: item.description,
            serial: item.serial,
            brand: "Venio" // Assuming brand, or get from item if available
        }));

        // Combine standard options with "Other" text
        const finalJobDetails = [...requestData.jobDetails];
        if (finalJobDetails.includes("อื่นๆ") && requestData.otherJobDetail) {
            // We keep "อื่นๆ" and the detail separate or combine them? 
            // Let's rely on the printer to handle it.
        }

        const payload = {
            ...requestData,
            requestDetails: {
                options: requestData.jobDetails,
                other: requestData.otherJobDetail
            },
            items
        };

        setLoading(true);
        try {
            const response = await fifoService.createRequest(payload);
            toast.success('สร้างรายการสำเร็จ!');

            // Set data for printing
            setSubmittedRequest({
                ...payload,
                documentId: response.documentId,
                date: new Date().toLocaleDateString('th-TH')
            });

            // Clear form
            setCart([]);
            setRequestData({
                customerName: '',
                customerAddress: '',
                wantDate: '',
                qcmDate: '',
                remark: '',
                departmentExpense: '',
                jobDetails: [],
                otherJobDetail: ''
            });
            fetchData(searchQuery);

        } catch (error) {
            toast.error('สร้างรายการไม่สำเร็จ: ' + (error.response?.data?.error || error.message));
        } finally {
            setLoading(false);
        }
    };

    // Printing logic removed - now handled by server PDF.
    // Use the live endpoint (always reflects current DB state), not
    // submittedRequest.pdfUrl — that's a static file snapshotted once at
    // creation time, which is the same stale-link mismatch the Telegram
    // notification used to have against this same button.
    const handleDownloadPdf = () => {
        if (submittedRequest?.documentId) {
            window.open(`${API_URL}/fifo/requests/${submittedRequest.documentId}/pdf`, '_blank');
        }
    };

    // If initial loading
    if (loading && !results) {
        return <PageLoader message="กำลังโหลดข้อมูลคลังสินค้า..." />;
    }

    return (
        <div>
            <div className="theme-page-header mb-4 no-print">
                <div className="d-flex justify-content-between align-items-start">
                    <div>
                        <h1 className="theme-page-title">
                            <i className="bi bi-cart-plus me-2"></i>
                            ใบแจ้งงาน QC (FIFO)
                        </h1>
                        <p className="theme-page-subtitle">
                            ค้นหาและเลือกเครื่องจากคลังสินค้าเพื่อสร้างใบแจ้งงาน QC สำหรับงาน Sale Co
                        </p>
                    </div>
                    <button
                        className="btn btn-outline-primary"
                        onClick={() => navigate('/all-requests')}
                    >
                        <i className="bi bi-list-ul me-2"></i>
                        ดูรายการเบิกทั้งหมด
                    </button>
                </div>
            </div>

            {/* SEARCH SECTION */}
            <div className="theme-card mb-4 no-print">
                <div className="theme-card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <h5 className="mb-0"><i className="bi bi-search me-2"></i>ค้นหาสินค้า</h5>
                    <div className="d-flex align-items-center gap-2">
                        <span className="text-muted small">
                            อัปเดตล่าสุด: {formatTimeAgo(refreshStatus.lastSyncedAt)}
                        </span>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={handleRefreshInventory}
                            disabled={refreshing || refreshStatus.onCooldown}
                            title={refreshStatus.onCooldown ? `รออีก ${formatCountdown(refreshStatus.retryAfterMs)} นาที` : 'ดึงข้อมูลสต็อกล่าสุดจาก NetSuite'}
                        >
                            <i className={`bi ${refreshing ? 'bi-arrow-repeat' : 'bi-arrow-clockwise'} me-1`}></i>
                            {refreshing
                                ? 'กำลังรีเฟรช...'
                                : refreshStatus.onCooldown
                                    ? `รีเฟรช (${formatCountdown(refreshStatus.retryAfterMs)})`
                                    : 'รีเฟรชข้อมูลสต็อก'}
                        </button>
                    </div>
                </div>
                <div className="theme-card-body">
                    <form onSubmit={handleSearch}>
                        <div className="row g-3">
                            <div className="col">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="ค้นหาด้วยชื่อสินค้า, รหัสสินค้า หรือซีเรียล..."
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                />
                            </div>
                            <div className="col-auto d-flex gap-2">
                                <button type="button" className="btn btn-secondary" onClick={handleReset}>
                                    ล้างค่า
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={loading}>
                                    <i className="bi bi-search me-2"></i>
                                    ค้นหา
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {/* RESULTS SECTION */}
            <div className="theme-card mb-4 no-print">
                <div className="theme-card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <i className="bi bi-list-check me-2"></i>
                        รายการสินค้าในคลัง
                    </h5>
                    <span className="badge bg-primary rounded-pill">
                        พบ {results?.remainingSerialsDetails?.length || 0} รายการ
                    </span>
                </div>
                <div className="theme-card-body p-0">
                    <div className="table-responsive" style={{ maxHeight: '500px' }}>
                        <table className="table theme-table table-hover mb-0">
                            <thead className="bg-light sticky-top" style={{ top: 0, zIndex: 1 }}>
                                <tr>
                                    <th style={{ width: '20%' }}>หมายเลขซีเรียล (S/N)</th>
                                    <th style={{ width: '15%' }}>รหัสสินค้า</th>
                                    <th style={{ width: '30%' }}>รายละเอียด</th>
                                    <th style={{ width: '15%' }}>วันที่รับเข้า</th>
                                    <th style={{ width: '10%' }}>สถานะ</th>
                                    <th style={{ width: '10%' }} className="text-end">เลือก</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr>
                                        <td colSpan="6" className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                {!loading && results?.remainingSerialsDetails?.map((item, idx) => {
                                    const isSelected = cart.some(c => c.serial === item.serial);
                                    return (
                                        <tr key={idx} className={isSelected ? 'table-active' : ''}>
                                            <td className="fw-medium">{item.serial}</td>
                                            <td className="text-muted small">{item.itemCode}</td>
                                            <td>
                                                <div className="text-truncate" style={{ maxWidth: '300px' }} title={item.description}>
                                                    {item.description}
                                                </div>
                                            </td>
                                            <td className="small">{new Date(item.lastReceiptDate).toLocaleDateString('th-TH')}</td>
                                            <td>
                                                <span className={`badge rounded-pill ${item.status === 'Available' ? 'bg-success' : 'bg-secondary'}`}>
                                                    {item.status === 'Available' ? 'ว่าง' : item.status}
                                                </span>
                                            </td>
                                            <td className="text-end">
                                                {item.status === 'Available' ? (
                                                    <button
                                                        className={`btn btn-sm ${isSelected ? 'btn-success' : 'btn-outline-primary'}`}
                                                        onClick={() => addToCart(item)}
                                                        disabled={isSelected}
                                                    >
                                                        {isSelected ? <i className="bi bi-check-lg"></i> : <i className="bi bi-plus-lg"></i>}
                                                    </button>
                                                ) : (
                                                    <span className="text-muted small">-</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {!loading && (!results?.remainingSerialsDetails || results.remainingSerialsDetails.length === 0) && (
                                    <tr>
                                        <td colSpan="6" className="text-center py-5 text-muted">
                                            ไม่พบรายการสินค้า
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* REQUEST FORM SECTION (Only visible if Cart has items) */}
            {cart.length > 0 && (
                <div className="theme-card border-primary no-print">
                    <div className="theme-card-header bg-primary text-white">
                        <h5 className="mb-0"><i className="bi bi-file-earmark-text me-2"></i>รายละเอียดการเบิก ({cart.length} รายการ)</h5>
                    </div>
                    <div className="theme-card-body">
                        <div className="row">
                            {/* Selected Items List */}
                            <div className="col-md-4">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="mb-0 text-muted">รายการที่เลือก</h6>
                                    <span className="badge bg-primary rounded-pill">{cart.length} รายการ</span>
                                </div>
                                <div className="d-flex flex-column gap-2" style={{ maxHeight: '420px', overflowY: 'auto', paddingRight: '4px' }}>
                                    {cart.map((item, idx) => (
                                        <div key={idx} style={{
                                            border: '1px solid #d0e8ff',
                                            borderLeft: '4px solid #0d6efd',
                                            borderRadius: '8px',
                                            padding: '10px 12px',
                                            backgroundColor: '#f0f7ff',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}>
                                            <div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <span style={{ fontSize: '11px', color: '#6c757d' }}>#{idx + 1}</span>
                                                    <span className="fw-bold" style={{ fontSize: '14px', color: '#0d47a1' }}>
                                                        <i className="bi bi-upc-scan me-1" style={{ fontSize: '12px' }}></i>
                                                        {item.serial}
                                                    </span>
                                                </div>
                                                <div className="text-muted" style={{ fontSize: '11px', marginTop: '2px' }}>
                                                    {item.itemCode}
                                                </div>
                                                {item.description && (
                                                    <div style={{ fontSize: '11px', color: '#495057', marginTop: '2px', maxWidth: '220px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                                        title={item.description}>
                                                        {item.description}
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                className="btn btn-sm"
                                                style={{ color: '#dc3545', padding: '2px 6px', lineHeight: 1 }}
                                                onClick={() => removeFromCart(item.serial)}
                                                title="ลบออก"
                                            >
                                                <i className="bi bi-x-lg"></i>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Form Details */}
                            <div className="col-md-8 ps-md-4">
                                <h6 className="mb-3 text-muted">ข้อมูลการแจ้งงาน</h6>

                                <div className="row g-3 mb-4">
                                    <div className="col-md-6">
                                        <label className="form-label">ชื่อลูกค้า / ตัวแทนสาขา <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={requestData.customerName}
                                            onChange={e => setRequestData({ ...requestData, customerName: e.target.value })}
                                            placeholder="ระบุชื่อลูกค้า..."
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">วันที่ต้องการใช้เครื่อง <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="DD/MM/YYYY"
                                            maxLength={10}
                                            value={requestData.wantDate}
                                            onChange={e => {
                                                let v = e.target.value.replace(/[^\d]/g, '');
                                                if (v.length >= 3) v = v.slice(0,2) + '/' + v.slice(2);
                                                if (v.length >= 6) v = v.slice(0,5) + '/' + v.slice(5,9);
                                                setRequestData({ ...requestData, wantDate: v });
                                            }}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">ต้องการรับเครื่องจากหน่วยงาน QCM วันที่</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="DD/MM/YYYY"
                                            maxLength={10}
                                            value={requestData.qcmDate}
                                            onChange={e => {
                                                let v = e.target.value.replace(/[^\d]/g, '');
                                                if (v.length >= 3) v = v.slice(0,2) + '/' + v.slice(2);
                                                if (v.length >= 6) v = v.slice(0,5) + '/' + v.slice(5,9);
                                                setRequestData({ ...requestData, qcmDate: v });
                                            }}
                                        />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label">ที่อยู่ / สถานที่ติดตั้ง</label>
                                        <textarea
                                            className="form-control"
                                            rows="2"
                                            value={requestData.customerAddress}
                                            onChange={e => setRequestData({ ...requestData, customerAddress: e.target.value })}
                                            placeholder="ระบุที่อยู่..."
                                        ></textarea>
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label">เก็บค่าใช้จ่ายที่</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={requestData.departmentExpense}
                                            onChange={e => setRequestData({ ...requestData, departmentExpense: e.target.value })}
                                            placeholder="ระบุแผนก/สถานที่เก็บค่าใช้จ่าย..."
                                        />
                                    </div>
                                </div>

                                <h6 className="mb-3 text-muted">รายละเอียดการแจ้งงาน (เลือกได้มากกว่า 1 ข้อ)</h6>
                                <div className="row g-2 mb-3">
                                    {JOB_OPTIONS.map((option, idx) => (
                                        <div key={idx} className="col-md-6">
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id={`job-${idx}`}
                                                    checked={requestData.jobDetails.includes(option)}
                                                    onChange={() => toggleJobDetail(option)}
                                                />
                                                <label className="form-check-label" htmlFor={`job-${idx}`}>
                                                    {option}
                                                </label>
                                            </div>
                                        </div>
                                    ))}
                                    {requestData.jobDetails.includes("อื่นๆ") && (
                                        <div className="col-12 mt-2">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="ระบุรายละเอียดอื่นๆ..."
                                                value={requestData.otherJobDetail}
                                                onChange={e => setRequestData({ ...requestData, otherJobDetail: e.target.value })}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">หมายเหตุเพิ่มเติม</label>
                                    <textarea
                                        className="form-control"
                                        rows="2"
                                        value={requestData.remark}
                                        onChange={e => setRequestData({ ...requestData, remark: e.target.value })}
                                    ></textarea>
                                </div>

                                <div className="text-end mt-4">
                                    <button
                                        className="btn btn-success btn-lg px-5 shadow-sm"
                                        onClick={handleSubmitRequest}
                                        disabled={loading}
                                    >
                                        {loading ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="bi bi-printer me-2"></i>}
                                        {loading ? 'กำลังบันทึก...' : 'บันทึกและพิมพ์ใบแจ้งงาน'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* PRINT PREVIEW MODAL / HIDDEN AREA */}
            {/* We render this hidden until printed, or show a modal after submit */}
            {submittedRequest && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">บันทึกสำเร็จ - กรุณาพิมพ์ใบแจ้งงาน</h5>
                                <button type="button" className="btn-close" onClick={() => setSubmittedRequest(null)}></button>
                            </div>
                            <div className="modal-body text-center">
                                <div className="mb-4">
                                    <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '4rem' }}></i>
                                    <h4 className="mt-2">สร้างรายการเรียบร้อยแล้ว</h4>
                                    <p className="text-muted">Document ID: {submittedRequest.documentId}</p>
                                </div>
                                <button className="btn btn-primary btn-lg px-5" onClick={handleDownloadPdf}>
                                    <i className="bi bi-printer-fill me-2"></i> พิมพ์เอกสาร (PDF)
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* HIDDEN PRINT COMPONENT */}
            <div style={{ overflow: 'hidden', height: 0 }}>
                <div ref={printRef} className="print-container p-4" style={{ fontFamily: 'Sarabun, sans-serif', fontSize: '14px', lineHeight: '1.5' }}>
                    <div className="text-center fw-bold fs-4 mb-4">ใบแจ้งงาน หน่วยงาน QCM</div>

                    <div className="mb-3">
                        <div className="fw-bold text-decoration-underline mb-2">ส่วนที่ 1 สำหรับลูกค้า</div>
                        <div className="d-flex justify-content-between mb-2">
                            <div>วันที่แจ้งงาน: {new Date().toLocaleDateString('th-TH')}</div>
                            <div>วันที่ต้องการใช้เครื่อง: {submittedRequest?.wantDate}</div>
                        </div>
                        <div className="d-flex mb-2">
                            <div style={{ minWidth: '150px' }}>ติดตั้งที่ / ชื่อลูกค้า:</div>
                            <div className="border-bottom flex-grow-1 border-dark ps-2">{submittedRequest?.customerName}</div>
                        </div>
                        <div className="d-flex mb-2">
                            <div style={{ minWidth: '150px' }}>ที่อยู่:</div>
                            <div className="border-bottom flex-grow-1 border-dark ps-2">{submittedRequest?.customerAddress}</div>
                        </div>
                        <div className="d-flex mb-3">
                            <div style={{ minWidth: '250px' }}>ต้องการรับเครื่องจากหน่วยงาน QCM วันที่:</div>
                            <div className="border-bottom flex-grow-1 border-dark ps-2">
                                {submittedRequest?.wantDate}
                            </div>
                        </div>

                        <div className="fw-bold mb-2">รายละเอียดการแจ้งงาน</div>
                        <div className="row g-2 mb-3">
                            {JOB_OPTIONS.map((opt, idx) => {
                                const isChecked = submittedRequest?.requestDetails?.options?.includes(opt);
                                const isOther = opt === "อื่นๆ";
                                return (
                                    <div key={idx} className="col-6">
                                        <div className="d-flex align-items-center">
                                            <div style={{
                                                width: '16px', height: '16px',
                                                border: '1px solid black', marginRight: '8px',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                                            }}>
                                                {isChecked && <i className="bi bi-check" style={{ fontSize: '18px', fontWeight: 'bold' }}></i>}
                                            </div>
                                            <span>
                                                {opt}
                                                {isOther && isChecked && submittedRequest?.requestDetails?.other && (
                                                    <span className="ms-2 text-decoration-underline">{submittedRequest.requestDetails.other}</span>
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <table className="table table-bordered border-dark mb-4">
                        <thead className="text-center">
                            <tr>
                                <th style={{ width: '60px' }}>ลำดับที่</th>
                                <th>เครื่องรุ่น</th>
                                <th style={{ width: '100px' }}>ยี่ห้อ</th>
                                <th>Serial No.</th>
                            </tr>
                        </thead>
                        <tbody>
                            {submittedRequest?.items?.map((item, idx) => (
                                <tr key={idx}>
                                    <td className="text-center">{idx + 1}</td>
                                    <td>
                                        <div>{item.productId}</div>
                                        <small>{item.description}</small>
                                    </td>
                                    <td className="text-center">{item.brand || 'Venio'}</td>
                                    <td>{item.serial}</td>
                                </tr>
                            ))}
                            {/* Empty rows to fill space if needed */}
                            {[...Array(Math.max(0, 5 - (submittedRequest?.items?.length || 0)))].map((_, i) => (
                                <tr key={`empty-${i}`} style={{ height: '35px' }}>
                                    <td></td><td></td><td></td><td></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className="row mb-5">
                        <div className="col-6 offset-6">
                            <div className="d-flex mb-2 align-items-end">
                                <div style={{ width: '100px' }}>ชื่อผู้แจ้งงาน:</div>
                                <div className="border-bottom border-dark flex-grow-1 ps-2">{submittedRequest?.userName || '...................................................'}</div>
                                <div className="ms-2">วันที่ {new Date().toLocaleDateString('th-TH')}</div>
                            </div>
                            <div className="d-flex mb-2 align-items-end">
                                <div style={{ width: '100px' }}>แผนกผู้แจ้งงาน:</div>
                                <div className="border-bottom border-dark flex-grow-1 ps-2">{submittedRequest?.department || 'ประสานงานขาย'}</div>
                            </div>
                            <div className="d-flex mb-2 align-items-end">
                                <div style={{ width: '100px' }}>เก็บค่าใช้จ่ายที่:</div>
                                <div className="border-bottom border-dark flex-grow-1 ps-2">-</div>
                            </div>
                            <div className="d-flex align-items-end">
                                <div style={{ width: '100px' }}>ผู้อนุมัติ:</div>
                                <div className="border-bottom border-dark flex-grow-1 ps-2"></div>
                                <div className="ms-2">วันที่ ....................</div>
                            </div>
                        </div>
                    </div>

                    <div className="fw-bold text-decoration-underline mb-2">ส่วนที่ 2 แจ้งผลการตรวจสอบ จากหน่วยงาน QCM</div>
                    <div className="border-bottom border-dark border-dotted mb-3" style={{ borderStyle: 'dotted', height: '30px' }}></div>
                    <div className="border-bottom border-dark border-dotted mb-3" style={{ borderStyle: 'dotted', height: '30px' }}></div>
                    <div className="border-bottom border-dark border-dotted mb-4" style={{ borderStyle: 'dotted', height: '30px' }}></div>

                    <div className="row">
                        <div className="col-6 offset-6">
                            <div className="d-flex mb-3 align-items-end">
                                <div style={{ width: '100px' }}>ช่างผู้ตรวจสอบ:</div>
                                <div className="border-bottom border-dark flex-grow-1"></div>
                                <div className="ms-2">วันที่ ....................</div>
                            </div>
                            <div className="d-flex align-items-end">
                                <div style={{ width: '100px' }}>ผู้อนุมัติ:</div>
                                <div className="border-bottom border-dark flex-grow-1"></div>
                                <div className="ms-2">วันที่ ....................</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {loading && cart.length > 0 && <PageLoader message="กำลังบันทึกและสร้างเอกสาร..." />}
        </div>
    );
};

export default RequestMachine;
